import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BodyPortal } from '../body-portal';
import { SearchableSelectOption } from './searchable-select-option';

const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;
const MIN_WIDTH = 240;
const MAX_HEIGHT = 320;

@Component({
  selector: 'tc-searchable-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.css',
  host: {
    '[class.ss-host--cell]': 'cell()',
  },
})
export class SearchableSelectComponent implements OnInit, OnDestroy {
  private readonly elRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly vcr = inject(ViewContainerRef);
  private readonly portal = new BodyPortal(
    this.vcr,
    this.renderer,
    this.document,
  );
  private readonly onScrollCapture = () => {
    if (this.open()) this.updatePosition();
  };

  @ViewChild('dropdownTpl', { read: TemplateRef })
  private dropdownTpl!: TemplateRef<unknown>;

  options = input<SearchableSelectOption[]>([]);
  value = input<string>('');
  cell = input(false);
  placeholder = input<string>('Vyberte...');
  searchPlaceholder = input<string>('Hledat...');
  noResultsLabel = input<string>('Žádné výsledky');
  allLabel = input<string>('');
  ariaLabel = input<string>('');
  valueChange = output<string>();

  readonly open = signal(false);
  readonly searchQuery = signal('');
  readonly dropdownTop = signal<number | null>(null);
  readonly dropdownBottom = signal<number | null>(null);
  readonly dropdownLeft = signal<number | null>(null);
  readonly dropdownWidth = signal(MIN_WIDTH);
  readonly dropdownMaxHeight = signal(MAX_HEIGHT);

  readonly selectedOption = computed(() => {
    const v = this.value();
    return v ? (this.options().find((o) => o.id === v) ?? null) : null;
  });

  readonly displayLabel = computed(() => {
    const selected = this.selectedOption();
    if (selected) return selected.label;
    return this.placeholder();
  });

  readonly filteredOptions = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    return q
      ? this.options().filter((o) => o.label.toLowerCase().includes(q))
      : this.options();
  });

  ngOnInit(): void {
    this.document.addEventListener('scroll', this.onScrollCapture, true);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('scroll', this.onScrollCapture, true);
    this.portal.detach();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (
      this.open() &&
      !this.elRef.nativeElement.contains(e.target as Node) &&
      !this.portal.contains(e.target)
    ) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.open()) this.updatePosition();
  }

  toggle() {
    if (this.open()) {
      this.close();
    } else {
      this.openDropdown();
    }
  }

  close() {
    this.open.set(false);
    this.searchQuery.set('');
    this.portal.detach();
  }

  select(id: string) {
    this.valueChange.emit(id);
    this.close();
  }

  selectFirstMatch() {
    const first = this.filteredOptions()[0];
    if (first) this.select(first.id);
  }

  showGroupHeader(opt: SearchableSelectOption, index: number): boolean {
    if (!opt.group) return false;
    const filtered = this.filteredOptions();
    return index === 0 || filtered[index - 1]?.group !== opt.group;
  }

  private openDropdown() {
    this.searchQuery.set('');
    this.open.set(true);
    this.portal.attach(this.dropdownTpl);
    this.updatePosition();
    setTimeout(() => {
      this.portal.querySelector<HTMLInputElement>('.ss-search')?.focus();
    }, 0);
  }

  private updatePosition(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.ss-trigger, .ss-trigger-cell',
    ) as HTMLElement | null;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const minWidth = this.cell() ? Math.min(MIN_WIDTH, viewportW - VIEWPORT_MARGIN * 2) : MIN_WIDTH;

    let width = Math.max(rect.width, minWidth);
    width = Math.min(width, viewportW - VIEWPORT_MARGIN * 2);
    this.dropdownWidth.set(width);

    const spaceBelow = viewportH - rect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
    const openUpward = spaceBelow < MAX_HEIGHT && spaceAbove > spaceBelow;
    const available = Math.max(openUpward ? spaceAbove : spaceBelow, 120);
    this.dropdownMaxHeight.set(Math.min(MAX_HEIGHT, available));

    if (openUpward) {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(viewportH - rect.top + DROPDOWN_GAP);
    } else {
      this.dropdownBottom.set(null);
      this.dropdownTop.set(rect.bottom + DROPDOWN_GAP);
    }

    let left = rect.left;
    if (left + width > viewportW - VIEWPORT_MARGIN) {
      left = viewportW - width - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }
    this.dropdownLeft.set(left);
  }
}
